import { type FormEvent, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Lock, Moon, Sun, UserRound } from 'lucide-react'
import { HttpError } from '../shared/api/http'
import { Button } from '../shared/ui/Button'
import { useAuth } from '../features/auth/state/useAuth'
import { AnimatedLogo } from '../features/landing/ui/AnimatedLogo'
import { useTheme } from '../shared/hooks/useTheme'
import { cn } from '@/lib/utils'

type NavigationState = {
  from?: string
}

export function LoginPage() {
  const { t, i18n } = useTranslation()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const auth = useAuth()

  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const from = (location.state as NavigationState | null)?.from || '/app'

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const normalizedIdentifier = identifier.trim()
    if (!normalizedIdentifier) {
      setError(t('auth.errors.identifierRequired'))
      return
    }
    if (!password.trim()) {
      setError(t('auth.errors.passwordRequired'))
      return
    }

    setError(null)

    try {
      await auth.loginWithPassword({ identifier: normalizedIdentifier, password })
      navigate(from === '/login' ? '/app' : from, { replace: true })
    } catch (requestError) {
      if (requestError instanceof HttpError && requestError.status === 401) {
        setError(t('auth.errors.invalidCredentials'))
        return
      }
      if (requestError instanceof Error) {
        setError(requestError.message)
        return
      }
      setError(t('auth.errors.loginFailed'))
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-8 sm:px-6">
      <div className="pointer-events-none absolute inset-0">
        <div
          className={cn(
            'login-glow-core absolute left-1/2 top-[46%] h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl',
            theme === 'dark'
              ? 'bg-gradient-to-tr from-primary/20 via-accent/16 to-transparent'
              : 'bg-[var(--glow-brand-strong)]',
          )}
        />
        <div
          className={cn(
            'login-glow-core absolute left-[50%] top-[42%] h-[460px] w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl',
            theme === 'dark' ? 'bg-cyan-400/14' : 'bg-cyan-300/70',
          )}
        />
        <div
          className={cn(
            'login-glow-a absolute left-[8%] top-[14%] h-52 w-52 rounded-full blur-3xl',
            theme === 'dark' ? 'border border-white/24 bg-white/14' : 'bg-sky-200/68',
          )}
        />
        <div
          className={cn(
            'login-glow-b absolute left-[24%] top-[10%] h-44 w-44 rounded-full blur-3xl',
            theme === 'dark' ? 'bg-fuchsia-400/15' : 'bg-fuchsia-200/56',
          )}
        />
        <div
          className={cn(
            'login-glow-c absolute right-[6%] top-[68%] h-56 w-56 rounded-full blur-3xl',
            theme === 'dark'
              ? 'border border-white/14 bg-primary/15'
              : 'bg-[var(--glow-brand-strong)]',
          )}
        />
        <div
          className={cn(
            'login-glow-a absolute right-[16%] top-[30%] h-44 w-44 rounded-full blur-3xl',
            theme === 'dark'
              ? 'bg-[var(--glow-secondary)]'
              : 'bg-[var(--glow-secondary)]',
          )}
        />
        <div
          className={cn(
            'login-glow-b absolute left-[18%] top-[66%] h-44 w-44 rounded-full blur-3xl',
            theme === 'dark' ? 'bg-emerald-400/14' : 'bg-fuchsia-200/52',
          )}
        />
        <div
          className={cn(
            'login-glow-center-x absolute left-[50%] top-[18%] h-48 w-48 -translate-x-1/2 rounded-full blur-3xl',
            theme === 'dark' ? 'bg-amber-300/14' : 'bg-amber-200/52',
          )}
        />
        <div
          className={cn(
            'login-glow-a absolute right-[22%] top-[20%] h-40 w-40 rounded-full blur-3xl',
            theme === 'dark' ? 'bg-sky-300/14' : 'bg-emerald-200/46',
          )}
        />
        <div
          className={cn(
            'login-glow-b absolute left-[60%] top-[72%] h-36 w-36 rounded-full blur-3xl',
            theme === 'dark' ? 'bg-rose-300/14' : 'bg-rose-200/46',
          )}
        />
        <div
          className={cn(
            'login-glow-c absolute left-[34%] top-[24%] h-40 w-40 rounded-full blur-3xl',
            theme === 'dark' ? 'hidden' : 'bg-blue-200/60',
          )}
        />
        <div
          className={cn(
            'login-glow-a absolute left-[72%] top-[44%] h-44 w-44 rounded-full blur-3xl',
            theme === 'dark' ? 'hidden' : 'bg-cyan-200/58',
          )}
        />
        <div
          className={cn(
            'login-glow-b absolute left-[46%] top-[78%] h-40 w-40 rounded-full blur-3xl',
            theme === 'dark' ? 'hidden' : 'bg-[var(--glow-secondary)]',
          )}
        />
        <div
          className={cn(
            'login-glow-a absolute left-[10%] top-[46%] h-36 w-36 rounded-full blur-3xl',
            theme === 'dark' ? 'hidden' : 'bg-emerald-200/48',
          )}
        />
      </div>

      <div className="relative w-full max-w-md rounded-2xl border border-border/80 bg-card/90 p-6 shadow-2xl backdrop-blur-xl sm:p-7">
        <div className="mb-6 flex items-center justify-between gap-3">
          <Link
            to="/"
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-opacity duration-150 hover:opacity-80',
              theme === 'dark'
                ? 'bg-white/[0.06] ring-1 ring-white/[0.08]'
                : 'bg-secondary ring-1 ring-border',
            )}
          >
            <AnimatedLogo size={22} />
          </Link>

          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-border bg-background/70 p-1">
              <button
                type="button"
                onClick={() => i18n.changeLanguage('en')}
                className={cn(
                  'h-7 rounded-md px-2 text-xs transition',
                  i18n.language === 'en'
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => i18n.changeLanguage('mn')}
                className={cn(
                  'h-7 rounded-md px-2 text-xs transition',
                  i18n.language === 'mn'
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                MN
              </button>
            </div>

            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background/70 text-muted-foreground transition hover:text-foreground"
              aria-label={t('topbar.theme')}
            >
              {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
            </button>
          </div>
        </div>

        <h1 className="m-0 text-2xl font-semibold text-foreground">{t('auth.title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('auth.description')}</p>

        <form className="mt-6 grid gap-4" onSubmit={handleSubmit}>
          <label className="grid gap-2" htmlFor="login-identifier">
            <span className="text-sm font-medium text-foreground">
              {t('auth.fields.identifier.label')}
            </span>
            <div className="relative">
              <UserRound
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                id="login-identifier"
                type="text"
                autoComplete="username"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                className="h-11 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm text-foreground outline-none transition focus-visible:ring-1 focus-visible:ring-ring"
                placeholder={t('auth.fields.identifier.placeholder')}
                disabled={auth.isLoginPending}
              />
            </div>
          </label>

          <label className="grid gap-2" htmlFor="login-password">
            <span className="text-sm font-medium text-foreground">
              {t('auth.fields.password.label')}
            </span>
            <div className="relative">
              <Lock
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="h-11 w-full rounded-lg border border-input bg-background pl-9 pr-10 text-sm text-foreground outline-none transition focus-visible:ring-1 focus-visible:ring-ring"
                placeholder={t('auth.fields.password.placeholder')}
                disabled={auth.isLoginPending}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
                aria-label={t('auth.fields.password.label')}
                disabled={auth.isLoginPending}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>

          {error ? (
            <p className="m-0 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            disabled={auth.isLoginPending}
            className={cn('h-11 w-full', 'bg-cta text-cta-foreground hover:bg-cta-hover')}
          >
            {auth.isLoginPending ? t('auth.actions.signingIn') : t('auth.actions.signIn')}
          </Button>
        </form>
      </div>
    </div>
  )
}
